import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { clearErrors, getUserDetails, updateUser } from '../../actions/userAction';
import { UPDATE_USER_RESET, REMOVE_USER_DETAILS } from '../../constants/userConstants';
import Loading from './Loading';
import Avatar from '@mui/material/Avatar';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { CameraAlt } from '@mui/icons-material';

const UpdateUser = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const params = useParams();
    const navigate = useNavigate();
    const theme = useTheme();

    const { user, error, loading } = useSelector((state) => state.userDetails);
    const { isUpdated, error: updateError, loading: updateLoading } = useSelector((state) => state.profile);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [gender, setGender] = useState("");
    const [role, setRole] = useState("");
    const [mobile, setMobile] = useState("");
    const [password, setPassword] = useState("");
    const [avatar, setAvatar] = useState("");
    const [avatarPreview, setAvatarPreview] = useState("");

    const userId = params.id;

    const handleAvatarChange = (e) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setAvatarPreview(reader.result);
                setAvatar(reader.result);
            }
        };
        reader.readAsDataURL(e.target.files[0]);
    };

    const updateUserSubmitHandler = (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.set("name", name);
        formData.set("email", email);
        formData.set("gender", gender);
        formData.set("role", role);
        formData.set("mobile", mobile);
        if (password) {
            formData.set("password", password);
        }
        if (avatar) {
            formData.set("avatar", avatar);
        }

        dispatch(updateUser(userId, formData));
    }

    useEffect(() => {
        if (user && user._id !== userId) {
            dispatch(getUserDetails(userId));
        } else if (user) {
            setName(user.name);
            setEmail(user.email);
            setGender(user.gender);
            setRole(user.role);
            setMobile(user.mobile);
            setAvatarPreview(user.avatar.url);
        }
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (updateError) {
            enqueueSnackbar(updateError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isUpdated) {
            enqueueSnackbar("User Updated Successfully", { variant: "success" });
            dispatch({ type: UPDATE_USER_RESET });
            dispatch({ type: REMOVE_USER_DETAILS });
            navigate("/admin/users");
        }
    }, [dispatch, error, userId, user, navigate, isUpdated, updateError, enqueueSnackbar]);

    return (
        <>
            <MetaData title="Admin: Update User | Flipkart" />

            {updateLoading && <BackdropLoader />}

            {loading ? <Loading /> : (
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="max-w-3xl mx-auto mt-10 p-6 sm:p-8 rounded-2xl shadow-xl"
                    style={{ backgroundColor: theme.palette.background.paper }}
                >
                    <h1 className="text-2xl font-bold text-center mb-8" style={{ color: theme.palette.text.primary }}>
                        Update User
                    </h1>

                    <form onSubmit={updateUserSubmitHandler} className="flex flex-col gap-6">
                        <div className="flex justify-center mb-4">
                            <div className="relative group">
                                <Avatar
                                    alt="Avatar Preview"
                                    src={avatarPreview}
                                    sx={{ width: 100, height: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
                                />
                                <label htmlFor="avatar-upload" className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 cursor-pointer rounded-full transition-opacity duration-300">
                                    <CameraAlt />
                                </label>
                                <input
                                    type="file"
                                    id="avatar-upload"
                                    accept="image/*"
                                    onChange={handleAvatarChange}
                                    className="hidden"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <TextField
                                fullWidth
                                label="Full Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                variant="outlined"
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                variant="outlined"
                            />
                            <TextField
                                fullWidth
                                label="Mobile"
                                type="text"
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value)}
                                variant="outlined"
                            />
                            <TextField
                                fullWidth
                                label="New Password (optional)"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                variant="outlined"
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                            <div>
                                <h3 className="text-md mb-2" style={{ color: theme.palette.text.secondary }}>Gender</h3>
                                <RadioGroup
                                    row
                                    value={gender}
                                    onChange={(e) => setGender(e.target.value)}
                                >
                                    <FormControlLabel value="male" control={<Radio />} label="Male" />
                                    <FormControlLabel value="female" control={<Radio />} label="Female" />
                                </RadioGroup>
                            </div>
                            <TextField
                                label="Role"
                                select
                                fullWidth
                                variant="outlined"
                                required
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                            >
                                <MenuItem value={"user"}>User</MenuItem>
                                <MenuItem value={"admin"}>Admin</MenuItem>
                            </TextField>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 mt-4">
                            <button
                                type="submit"
                                className="w-full py-3 rounded-lg font-medium shadow-lg transition-transform transform hover:scale-105"
                                style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}
                            >
                                Update
                            </button>
                            <Link
                                to="/admin/users"
                                className="w-full py-3 text-center rounded-lg font-medium shadow-lg transition-transform transform hover:scale-105"
                                style={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary, border: `1px solid ${theme.palette.divider}` }}
                            >
                                Cancel
                            </Link>
                        </div>
                    </form>
                </motion.div>
            )}
        </>
    );
};

export default UpdateUser;
